import React, { useState, useEffect, useCallback } from 'react';
import { Car, Calendar, Eye, CheckCircle, X, Search, MapPin } from 'lucide-react';
import apiService from '../apiService';
import { useLiveRefresh } from '../liveRefresh';
import { useAdmin } from '../context/AdminContext';
import Img from '../components/Img';
import CityFilter, { useCities, cityLabel } from '../components/CityFilter';

export default function ClientValidationPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchQ, setSearchQ] = useState('');
  const [cityFilter, setCityFilter] = useState(''); // '' = toutes les villes
  const cities = useCities();
  const [selectedClient, setSelectedClient] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      // Paramètres dans l'URL : apiService.get de l'admin ignore l'objet de paramètres.
      const cityQ = cityFilter ? `city_slug=${encodeURIComponent(cityFilter)}` : '';
      const endpoint = filter === 'pending'
        ? `/admin/identity/pending${cityQ ? `?${cityQ}` : ''}`
        : `/admin/identity/all?status=${filter}${searchQ ? `&search=${encodeURIComponent(searchQ)}` : ''}${cityQ ? `&${cityQ}` : ''}`;
      const data = await apiService.get(endpoint, { userType: 'admin' });
      if (data.success) setClients(data.users || []);
    } catch (e) { console.error('Error loading clients:', e); }
    finally { setLoading(false); }
  }, [filter, searchQ, cityFilter]);

  useEffect(() => { loadClients(); }, [loadClients]);
  useLiveRefresh(() => loadClients(), ['identity_verification']);

  const handleApprove = async (userId) => {
    if (!window.confirm("Approuver l'identité de ce client ?")) return;
    setActionLoading(true);
    try {
      const data = await apiService.post(`/admin/identity/${userId}/approve`, {}, { userType: 'admin' });
      if (data.success) {
        alert('✅ Identité approuvée ! Le compte solde du client est maintenant actif.');
        loadClients();
        setSelectedClient(null);
      }
    } catch (e) { alert('Erreur: ' + (e.message || 'Erreur')); }
    finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { alert('Veuillez saisir un motif de rejet'); return; }
    setActionLoading(true);
    try {
      const data = await apiService.post(`/admin/identity/${rejectTarget}/reject`, { reason: rejectReason.trim() }, { userType: 'admin' });
      if (data.success) {
        alert('❌ Identité rejetée. Le client sera notifié.');
        setShowRejectModal(false); setRejectReason(''); setRejectTarget(null);
        loadClients(); setSelectedClient(null);
      }
    } catch (e) { alert('Erreur: ' + (e.message || 'Erreur')); }
    finally { setActionLoading(false); }
  };

  const statusBadge = (status) => {
    const map = {
      not_submitted: { bg: '#F4F5F7', color: '#5C636E', label: 'Non soumis' },
      pending: { bg: '#fef3c7', color: '#FF9B00', label: 'En attente' },
      approved: { bg: '#d1fae5', color: '#059669', label: 'Approuvé' },
      rejected: { bg: '#fee2e2', color: '#dc2626', label: 'Rejeté' },
    };
    const s = map[status] || map.not_submitted;
    return <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600', backgroundColor: s.bg, color: s.color }}>{s.label}</span>;
  };

  const docTypeLabel = (type) => {
    const map = { carte_identite: "Carte d'identité", passeport: 'Passeport', permis_conduire: 'Permis de conduire', carte_electeur: "Carte d'électeur", carte_etudiant: 'Carte étudiant' };
    return map[type] || type;
  };

  const cardStyle = { background: 'white', borderRadius: '14px', padding: '1.2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '12px' };

  return (
    <div className="dashboard-content">
      <h1 className="page-title">Validation Clients</h1>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        {['pending', 'approved', 'rejected', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 16px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem',
            backgroundColor: filter === f ? '#FFB900' : '#F4F5F7', color: filter === f ? 'white' : '#5C636E'
          }}>
            {f === 'pending' ? 'En attente' : f === 'approved' ? 'Approuvés' : f === 'rejected' ? 'Rejetés' : 'Tous'}
          </button>
        ))}
        <CityFilter value={cityFilter} onChange={setCityFilter} cities={cities} className=""
          style={{ padding: '8px 14px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '0.85rem', background: 'white', marginLeft: '8px' }} />
        {filter !== 'pending' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
            <Search size={18} color="#9AA0A8" />
            <input type="text" placeholder="Rechercher..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
              style={{ padding: '8px 14px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '0.9rem', width: '200px' }} />
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement...</p></div>
      ) : clients.length === 0 ? (
        <div className="empty-state"><CheckCircle size={64} /><h3>Aucun client {filter === 'pending' ? 'en attente' : ''}{cityFilter ? ` à ${cityLabel(cities, cityFilter)}` : ''}</h3></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
          {clients.map(client => (
            <div key={client.user_id} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                {client.photo ? (
                  <Img src={client.photo} alt="" style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#F4F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#9AA0A8', fontSize: '1.1rem' }}>
                    {client.name?.charAt(0) || '?'}
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '700', color: '#1C1C1C' }}>{client.name}</div>
                  <div style={{ fontSize: '0.85rem', color: '#5C636E' }}>{client.phone}</div>
                </div>
                {statusBadge(client.identity_status)}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#5C636E', marginBottom: '8px' }}>
                <strong>Document:</strong> {docTypeLabel(client.identity_document_type)}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#5C636E', marginBottom: '8px' }}>
                <MapPin size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {cityLabel(cities, client.city_slug)}
              </div>
              {client.identity_submitted_at && (
                <div style={{ fontSize: '0.8rem', color: '#9AA0A8', marginBottom: '10px' }}>
                  <Calendar size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Soumis le {new Date(client.identity_submitted_at).toLocaleDateString('fr-FR')}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setSelectedClient(client)} style={{ flex: 1, padding: '8px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: '#FF7800', color: 'white', fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <Eye size={16} /> Voir
                </button>
                {client.identity_status === 'pending' && (
                  <>
                    <button onClick={() => handleApprove(client.user_id)} disabled={actionLoading} style={{ padding: '8px 14px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: '#10b981', color: 'white', fontWeight: '600', fontSize: '0.85rem' }}>✅</button>
                    <button onClick={() => { setRejectTarget(client.user_id); setShowRejectModal(true); }} style={{ padding: '8px 14px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: '#DC2626', color: 'white', fontWeight: '600', fontSize: '0.85rem' }}>❌</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedClient && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setSelectedClient(null)}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '700px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, color: '#1C1C1C' }}>Vérification — {selectedClient.name}</h2>
              <button onClick={() => setSelectedClient(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', alignItems: 'center' }}>
              <span style={{ fontWeight: '600', color: '#5C636E' }}>Statut:</span>
              {statusBadge(selectedClient.identity_status)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '1rem' }}>
              <div><span style={{ color: '#5C636E', fontSize: '0.85rem' }}>Nom:</span><div style={{ fontWeight: '600' }}>{selectedClient.name}</div></div>
              <div><span style={{ color: '#5C636E', fontSize: '0.85rem' }}>Téléphone:</span><div style={{ fontWeight: '600' }}>{selectedClient.phone}</div></div>
              <div><span style={{ color: '#5C636E', fontSize: '0.85rem' }}>Email:</span><div style={{ fontWeight: '600' }}>{selectedClient.email || '—'}</div></div>
              <div><span style={{ color: '#5C636E', fontSize: '0.85rem' }}>Ville:</span><div style={{ fontWeight: '600' }}>{cityLabel(cities, selectedClient.city_slug)}</div></div>
              <div><span style={{ color: '#5C636E', fontSize: '0.85rem' }}>Type document:</span><div style={{ fontWeight: '600' }}>{docTypeLabel(selectedClient.identity_document_type)}</div></div>
            </div>
            {selectedClient.identity_rejection_reason && (
              <div style={{ background: '#fee2e2', padding: '12px', borderRadius: '10px', marginBottom: '1rem' }}>
                <strong style={{ color: '#dc2626' }}>Motif de rejet:</strong>
                <div style={{ color: '#7f1d1d', marginTop: '4px' }}>{selectedClient.identity_rejection_reason}</div>
              </div>
            )}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ marginBottom: '12px', color: '#414852' }}>Documents soumis</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {selectedClient.identity_document_url && (
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#5C636E', marginBottom: '6px' }}>📄 Document d'identité</div>
                    <Img src={selectedClient.identity_document_url} alt="Document" style={{ width: '100%', borderRadius: '10px', border: '1px solid #E9EBEF', maxHeight: '300px', objectFit: 'contain', background: '#FFFFFF' }} />
                  </div>
                )}
                {selectedClient.identity_selfie_url && (
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#5C636E', marginBottom: '6px' }}>🤳 Selfie</div>
                    <Img src={selectedClient.identity_selfie_url} alt="Selfie" style={{ width: '100%', borderRadius: '10px', border: '1px solid #E9EBEF', maxHeight: '300px', objectFit: 'contain', background: '#FFFFFF' }} />
                  </div>
                )}
              </div>
            </div>
            {selectedClient.identity_status === 'pending' && (
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => handleApprove(selectedClient.user_id)} disabled={actionLoading} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', cursor: 'pointer', background: '#10b981', color: 'white', fontWeight: '700', fontSize: '1rem', opacity: actionLoading ? 0.5 : 1 }}>✅ Approuver</button>
                <button onClick={() => { setRejectTarget(selectedClient.user_id); setShowRejectModal(true); }} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', cursor: 'pointer', background: '#DC2626', color: 'white', fontWeight: '700', fontSize: '1rem' }}>❌ Rejeter</button>
              </div>
            )}
          </div>
        </div>
      )}

      {showRejectModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
          onClick={() => { setShowRejectModal(false); setRejectReason(''); }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '450px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1rem', color: '#1C1C1C' }}>Motif du rejet</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Expliquez pourquoi les documents sont rejetés..."
              style={{ width: '100%', minHeight: '100px', padding: '12px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '0.95rem', resize: 'vertical', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: '12px', marginTop: '1rem' }}>
              <button onClick={() => { setShowRejectModal(false); setRejectReason(''); }} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #E9EBEF', background: 'white', cursor: 'pointer', fontWeight: '600' }}>Annuler</button>
              <button onClick={handleReject} disabled={actionLoading} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: '#DC2626', color: 'white', cursor: 'pointer', fontWeight: '600', opacity: actionLoading ? 0.5 : 1 }}>Confirmer le rejet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
